import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { featuredCampaigns } from '@/mocks/homeData';
import { campaignDetails } from '@/mocks/campaignDetailData';

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const campaignId = Number(id);
  const campaign = featuredCampaigns.find((c) => c.id === campaignId);
  const detail = campaignDetails[campaignId];
  const [activeTab, setActiveTab] = useState<'story' | 'updates' | 'documents' | 'gallery'>('story');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');

  const presetAmounts = [250, 500, 1000, 2500, 5000, 10000];

  if (!campaign) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-20 md:pt-28 pb-16 bg-background-50 flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Campaign Not Found</h1>
            <p className="text-foreground-500 text-sm mb-6">The campaign you're looking for doesn't exist or has been removed.</p>
            <Link to="/campaigns" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-semibold rounded-full hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line"></i>Browse Campaigns
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => setLightboxOpen(false);
  const prevImage = () => {
    if (!detail) return;
    setLightboxIndex((prev) => (prev === 0 ? detail.galleryImages.length - 1 : prev - 1));
  };
  const nextImage = () => {
    if (!detail) return;
    setLightboxIndex((prev) => (prev === detail.galleryImages.length - 1 ? 0 : prev + 1));
  };

  const progressPct = Math.round((campaign.raised / campaign.goal) * 100);
  const urgencyColors: Record<string, string> = { critical: 'bg-red-500', high: 'bg-accent-500', medium: 'bg-primary-500', low: 'bg-secondary-500' };
  const urgencyBadges: Record<string, string> = { critical: 'Critical', high: 'Urgent', medium: 'Active', low: 'Ongoing' };

  const tabs = [
    { key: 'story' as const, label: 'Story', icon: 'ri-book-open-line' },
    { key: 'updates' as const, label: 'Updates', icon: 'ri-notification-3-line', badge: detail?.updates.length },
    { key: 'documents' as const, label: 'Documents', icon: 'ri-file-text-line', badge: detail?.documents.length },
    { key: 'gallery' as const, label: 'Gallery', icon: 'ri-image-line', badge: detail?.galleryImages.length },
  ];

  return (
    <>
      <Navbar />
      <main>
        {/* Hero Banner */}
        <section className="relative pt-20 md:pt-28 pb-0 bg-foreground-950 overflow-hidden">
          <div className="relative h-[320px] md:h-[420px]">
            <img
              src={campaign.imageUrl}
              alt={campaign.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/60 to-foreground-950/30"></div>
            <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 lg:p-8">
              <div className="max-w-[1200px] mx-auto">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-xs font-medium rounded-full">{campaign.category}</span>
                  <span className={`px-3 py-1 text-white text-xs font-semibold rounded-full ${urgencyColors[campaign.urgency]}`}>{urgencyBadges[campaign.urgency]}</span>
                  <span className="px-3 py-1 bg-white/20 backdrop-blur-sm text-white/80 text-xs rounded-full">{campaign.daysLeft} days left</span>
                </div>
                <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-3 max-w-4xl">{campaign.title}</h1>
                <p className="text-white/70 text-sm md:text-base max-w-2xl line-clamp-2">{campaign.story}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">

              {/* Main Content */}
              <div className="lg:col-span-2">
                {/* Progress Card */}
                <div className="bg-white rounded-2xl p-5 md:p-6 border border-background-200 mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <span className="text-2xl md:text-3xl font-bold text-foreground-950">₹{campaign.raised.toLocaleString()}</span>
                      <span className="text-foreground-400 text-sm ml-2">raised of ₹{campaign.goal.toLocaleString()}</span>
                    </div>
                    <span className="text-sm font-bold text-foreground-900">{progressPct}%</span>
                  </div>
                  <div className="w-full h-3 bg-background-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-primary-400 to-primary-500 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.min(progressPct, 100)}%` }}
                    ></div>
                  </div>
                  <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-4 text-xs text-foreground-500">
                    <span><i className="ri-user-line mr-1"></i> {campaign.donors.toLocaleString()} donors</span>
                    <span><i className="ri-time-line mr-1"></i> {campaign.daysLeft} days remaining</span>
                    {detail && <span><i className="ri-team-line mr-1"></i> {detail.beneficiaryCount.toLocaleString()} beneficiaries</span>}
                  </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-2xl border border-background-200 overflow-hidden mb-6">
                  <div className="flex border-b border-background-200 overflow-x-auto">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-1.5 px-4 md:px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-[1px] ${
                          activeTab === tab.key
                            ? 'border-primary-500 text-primary-600'
                            : 'border-transparent text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        <i className={`${tab.icon} text-base`}></i>
                        {tab.label}
                        {tab.badge !== undefined && (
                          <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                            activeTab === tab.key ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-500'
                          }`}>{tab.badge}</span>
                        )}
                      </button>
                    ))}
                  </div>

                  <div className="p-5 md:p-6">
                    {/* Story Tab */}
                    {activeTab === 'story' && (
                      <div>
                        {detail ? (
                          <div className="prose prose-sm max-w-none">
                            {detail.fullDescription.split('\n\n').map((paragraph, i) => (
                              <p key={i} className="text-foreground-700 text-sm leading-relaxed mb-4 last:mb-0">{paragraph}</p>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                              <i className="ri-file-text-line text-foreground-400 text-2xl"></i>
                            </div>
                            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">Detailed story coming soon</h3>
                            <p className="text-foreground-500 text-sm">We're working on adding the complete campaign story. Check back soon for updates.</p>
                          </div>
                        )}
                        {detail && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 pt-6 border-t border-background-200">
                            <div className="text-center">
                              <div className="text-lg font-bold text-foreground-950">{detail.organizer}</div>
                              <div className="text-xs text-foreground-400">Organized By</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-foreground-950">{detail.location}</div>
                              <div className="text-xs text-foreground-400">Location</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-foreground-950">{new Date(detail.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                              <div className="text-xs text-foreground-400">Started</div>
                            </div>
                            <div className="text-center">
                              <div className="text-lg font-bold text-foreground-950">{detail.beneficiaryCount.toLocaleString()}</div>
                              <div className="text-xs text-foreground-400">Beneficiaries</div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Updates Tab */}
                    {activeTab === 'updates' && (
                      <div>
                        {detail && detail.updates.length > 0 ? (
                          <div className="space-y-0">
                            {detail.updates.map((update, i) => (
                              <div key={i} className={`flex gap-4 ${i < detail.updates.length - 1 ? 'pb-5 mb-5 border-b border-background-200' : ''}`}>
                                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                                  <i className="ri-chat-3-line text-primary-600 text-lg"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs text-foreground-400">{new Date(update.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                                  </div>
                                  <h4 className="text-sm font-bold text-foreground-950 mb-1.5">{update.title}</h4>
                                  <p className="text-foreground-600 text-sm leading-relaxed">{update.content}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                              <i className="ri-notification-3-line text-foreground-400 text-2xl"></i>
                            </div>
                            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">No updates yet</h3>
                            <p className="text-foreground-500 text-sm">Campaign updates will appear here once the team posts them.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                      <div>
                        {detail && detail.documents.length > 0 ? (
                          <div className="space-y-3">
                            {detail.documents.map((doc, i) => (
                              <div key={i} className="flex items-center gap-4 p-4 bg-background-50 rounded-xl border border-background-200 hover:border-primary-300 transition-colors cursor-pointer group">
                                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                                  <i className="ri-file-pdf-line text-primary-600 text-xl"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-bold text-foreground-950 mb-0.5">{doc.name}</h4>
                                  <p className="text-xs text-foreground-500">{doc.desc}</p>
                                </div>
                                <div className="flex-shrink-0 text-xs font-semibold text-foreground-400 flex items-center gap-1">
                                  <span className="px-2 py-1 bg-background-200 rounded-md">{doc.type}</span>
                                  <i className="ri-download-line text-lg text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                              <i className="ri-file-text-line text-foreground-400 text-2xl"></i>
                            </div>
                            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">No documents available</h3>
                            <p className="text-foreground-500 text-sm">Campaign documents will be uploaded by the team as the campaign progresses.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Gallery Tab */}
                    {activeTab === 'gallery' && (
                      <div>
                        {detail && detail.galleryImages.length > 0 ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {detail.galleryImages.map((img, i) => (
                              <button
                                key={i}
                                onClick={() => openLightbox(i)}
                                className="aspect-square rounded-xl overflow-hidden bg-background-200 cursor-pointer group relative"
                              >
                                <img
                                  src={img}
                                  alt={`Gallery image ${i + 1}`}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/20 transition-colors flex items-center justify-center">
                                  <i className="ri-zoom-in-line text-white text-2xl opacity-0 group-hover:opacity-100 transition-opacity"></i>
                                </div>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-10">
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                              <i className="ri-image-line text-foreground-400 text-2xl"></i>
                            </div>
                            <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">No images yet</h3>
                            <p className="text-foreground-500 text-sm">Campaign photos will be added as the team captures field updates.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Donate Card */}
                <div className="bg-white rounded-2xl p-5 md:p-6 border border-background-200 sticky top-24">
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">Make a Donation</h3>
                  <div className="grid grid-cols-3 gap-2 mb-4">
                    {presetAmounts.map((amount) => (
                      <button
                        key={amount}
                        onClick={() => { setSelectedAmount(amount); setCustomAmount(''); }}
                        className={`py-2.5 rounded-xl border text-sm font-semibold transition-all ${
                          selectedAmount === amount
                            ? 'border-primary-500 bg-primary-50 text-primary-600'
                            : 'border-background-200 bg-background-50 text-foreground-700 hover:border-primary-300'
                        }`}
                      >
                        ₹{amount.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <div className="relative mb-4">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm">₹</span>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                      placeholder="Custom amount"
                      min="10"
                      className="w-full pl-8 pr-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-xl focus:outline-none focus:border-primary-400 transition-all text-foreground-900 placeholder:text-foreground-400"
                    />
                  </div>
                  <Link
                    to={`/donate?campaign=${campaignId}&amount=${selectedAmount || customAmount || ''}`}
                    className="block w-full text-center py-3 bg-primary-500 text-white text-sm font-bold rounded-xl hover:bg-primary-600 transition-colors mb-3"
                  >
                    <i className="ri-heart-line mr-2"></i>Donate Now
                  </Link>
                  <button className="w-full py-2.5 text-foreground-500 text-xs font-medium rounded-xl hover:bg-background-100 transition-colors flex items-center justify-center gap-1.5">
                    <i className="ri-share-forward-line"></i>Share Campaign
                  </button>

                  <div className="mt-5 pt-5 border-t border-background-200 space-y-2.5">
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <i className="ri-shield-check-line text-green-600"></i> Verified campaign
                    </div>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <i className="ri-refund-2-line text-green-600"></i> 80G tax benefit available
                    </div>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <i className="ri-lock-line text-green-600"></i> Secure payment
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Lightbox */}
        {lightboxOpen && detail && (
          <div className="fixed inset-0 z-[100] bg-foreground-950/95 flex items-center justify-center" onClick={closeLightbox}>
            <button
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors z-10"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <i className="ri-arrow-left-s-line text-xl"></i>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            >
              <i className="ri-arrow-right-s-line text-xl"></i>
            </button>
            <img
              src={detail.galleryImages[lightboxIndex]}
              alt={`Gallery image ${lightboxIndex + 1}`}
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-xs">
              {lightboxIndex + 1} / {detail.galleryImages.length}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </>
  );
}